import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import CourseDiscussions from '../components/courses/CourseDiscussions'
import CourseReviews from '../components/courses/CourseReviews'
import { accessLine, certificatePath, lessonPath } from '../components/courses/courseUtils'
import { lessonsInOrder, useCourse } from '../components/courses/useCourse'
import { coursesAPI, monetizationAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { CourseCertificate } from '../types'
import { apiErrorMessage } from '../utils/apiError'

export const COURSE_DISCLAIMER =
  "Not financial advice. This course teaches; it doesn't tell you what to buy or sell. Do your own research."

const LEVELS: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
}

/**
 * A course's public page (/learn/<slug>): what it covers, its outline, and
 * the way in. Free courses are joined in one tap; a course included with
 * subscription tiers is joined by a subscriber on one of them, and anyone
 * else is told which tiers and pointed at the creator's page. Lessons open
 * for students, and free previews for anyone.
 */
const CoursePage = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const { state, reload } = useCourse(slug)
  const [tierNames, setTierNames] = useState<Record<number, string>>({})
  const [joining, setJoining] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [certificate, setCertificate] = useState<CourseCertificate | null>(null)

  const completedCourse = state.kind === 'ready' && state.enrollment?.status === 'completed' ? state.course.slug : null
  useEffect(() => {
    if (!completedCourse) return
    coursesAPI
      .myCertificates()
      .then((list) => setCertificate(list.find((c) => c.course_slug === completedCourse) || null))
      .catch(() => undefined)
  }, [completedCourse])

  const instructorId = state.kind === 'ready' ? state.course.instructor_id : null
  const hasTiers = state.kind === 'ready' && state.course.included_tier_ids.length > 0
  useEffect(() => {
    if (!instructorId || !hasTiers) return
    monetizationAPI
      .getTiers(instructorId)
      .then((tiers) => setTierNames(Object.fromEntries(tiers.map((t) => [t.id, t.name]))))
      .catch(() => undefined)
  }, [instructorId, hasTiers])

  if (state.kind !== 'ready') {
    return (
      <PublicShell>
        {state.kind === 'loading' ? (
          <div className="flex justify-center py-16" data-testid="course-loading">
            <Spinner />
          </div>
        ) : (
          <div className="py-16 text-center" data-testid={`course-${state.kind}`}>
            <h1 className="text-xl font-semibold text-gray-100">
              {state.kind === 'missing' ? 'No course here' : 'This course could not be loaded'}
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              {state.kind === 'missing' ? 'It may have moved, or not be published yet.' : 'Try again in a minute.'}
            </p>
          </div>
        )}
      </PublicShell>
    )
  }

  const { course, modules, enrollment, completed, manages } = state
  const lessons = lessonsInOrder(modules)
  const nextLesson = lessons.find((l) => !completed.has(l.id)) || lessons[0]
  const canRead = Boolean(enrollment) || manages
  const done = lessons.filter((l) => completed.has(l.id)).length

  const join = async () => {
    setJoining(true)
    setRefusal(null)
    try {
      await coursesAPI.enroll(course.id)
      await reload()
    } catch (error) {
      setRefusal(apiErrorMessage(error, "Couldn't join the course."))
    } finally {
      setJoining(false)
    }
  }

  return (
    <PublicShell>
      <article className="space-y-6" data-testid="course-page">
        <header>
          <p className="text-xs uppercase tracking-wider text-gray-500">
            Course{course.instructor_username ? ` by @${course.instructor_username}` : ''} · {LEVELS[course.level] || course.level}
          </p>
          <h1 className="mt-1 text-3xl font-bold text-gray-50">{course.title}</h1>
          {course.subtitle && <p className="mt-1 text-lg text-gray-300">{course.subtitle}</p>}
          <p className="mt-2 text-sm font-medium text-accent" data-testid="course-access">
            {accessLine(course, tierNames)}
          </p>
          {course.status !== 'published' && (
            <p className="mt-2 text-sm text-amber-300" data-testid="course-draft-note">
              Draft: only you can see this page until the course is published.
            </p>
          )}
        </header>

        <section className="rounded-xl border border-terminal-border bg-terminal-surface p-5 text-sm text-gray-300">
          <p className="whitespace-pre-line">{course.description}</p>
          {course.learning_objectives.length > 0 && (
            <>
              <h2 className="mt-4 text-xs font-medium uppercase tracking-wider text-gray-500">What you&rsquo;ll learn</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {course.learning_objectives.map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
            </>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-3" data-testid="course-cta">
          {canRead && nextLesson ? (
            <>
              <Link
                to={lessonPath(course.slug, nextLesson.id)}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {done === 0 ? 'Start the course' : 'Continue'}
              </Link>
              {enrollment && lessons.length > 0 && (
                <span className="text-sm text-gray-400" data-testid="course-progress">
                  {done} of {lessons.length} lessons done
                </span>
              )}
            </>
          ) : !signedIn ? (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Sign in to join
            </button>
          ) : course.access === 'paid' ? (
            <p className="text-sm text-gray-400">This course isn&rsquo;t open yet.</p>
          ) : (
            <button
              type="button"
              onClick={join}
              disabled={joining}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {joining ? 'Joining…' : 'Join the course'}
            </button>
          )}
        </div>
        {certificate && (
          <p
            className="rounded-xl border border-success-500/40 bg-success-500/10 p-4 text-sm text-gray-200"
            data-testid="course-certificate"
          >
            You completed this course.{' '}
            <Link to={certificatePath(certificate.certificate_number)} className="font-medium text-sky-300 underline">
              Your certificate
            </Link>{' '}
            ({certificate.certificate_number}): a record of completion, not a qualification.
          </p>
        )}
        {refusal && (
          <p role="alert" className="text-sm text-amber-300" data-testid="course-refusal">
            {refusal}{' '}
            {course.access === 'subscribers' && (
              <Link to={`/creator/${course.instructor_id}`} className="font-medium text-sky-300 underline">
                See the subscriptions
              </Link>
            )}
          </p>
        )}

        <section aria-labelledby="course-outline">
          <h2 id="course-outline" className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
            Outline
          </h2>
          {modules.length === 0 ? (
            <p className="text-sm text-gray-400">No lessons yet.</p>
          ) : (
            <ol className="space-y-4" data-testid="course-outline">
              {modules.map((module) => (
                <li key={module.id} className="rounded-xl border border-terminal-border bg-terminal-surface">
                  <h3 className="border-b border-terminal-border px-4 py-2 font-semibold text-gray-100">{module.title}</h3>
                  <ul className="divide-y divide-terminal-border">
                    {module.lessons.map((lesson) => {
                      const open = canRead || lesson.is_preview
                      return (
                        <li key={lesson.id} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid="outline-lesson">
                          <span className="w-4 text-success-500" aria-label={completed.has(lesson.id) ? 'Done' : undefined}>
                            {completed.has(lesson.id) ? '✓' : ''}
                          </span>
                          {open ? (
                            <Link to={lessonPath(course.slug, lesson.id)} className="text-gray-100 hover:text-sky-300">
                              {lesson.title}
                            </Link>
                          ) : (
                            <span className="text-gray-400">{lesson.title}</span>
                          )}
                          {lesson.is_preview && !canRead && (
                            <span className="rounded bg-sky-900/50 px-1.5 py-0.5 text-xs text-sky-200">Free preview</span>
                          )}
                          {lesson.duration_minutes ? (
                            <span className="ml-auto text-xs text-gray-500">{lesson.duration_minutes} min</span>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </section>

        {course.status === 'published' && <CourseReviews courseId={course.id} />}
        {canRead && course.status === 'published' && <CourseDiscussions courseId={course.id} manages={manages} />}

        <p className="text-xs text-gray-500" data-testid="course-disclaimer">
          {COURSE_DISCLAIMER}
        </p>
      </article>
    </PublicShell>
  )
}

export default CoursePage
